import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const TITLE = "Termos de uso | Nook Gym";
const DESCRIPTION =
  "Condições de uso do site institucional da Nook Gym, incluindo conteúdo, marcas, links para canais oficiais e regras de privacidade.";
const LAST_UPDATE = "18/08/2026";

export const Route = createFileRoute("/termos-de-uso")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://nookgym.com.br/termos-de-uso" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://nookgym.com.br/termos-de-uso" }],
  }),
  component: Page,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}

function Page() {
  return (
    <section className="pb-20 pt-28">
      <div className="mx-auto max-w-3xl px-5 lg:px-8">
        <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Termos de uso" }]} />
        <h1 className="display mt-8 text-4xl sm:text-5xl">Termos de uso</h1>
        <p className="mt-4 text-xs uppercase tracking-[0.16em] text-muted-foreground">
          Última atualização: {LAST_UPDATE}
        </p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-muted-foreground">
          <Section title="1. Objeto">
            <p>
              O site possui finalidade institucional e informativa, apresentando informações sobre a
              Nook Gym, seu método de treinamento, unidades, serviços, horários, oportunidades de
              treino experimental e canais de atendimento. A utilização do site está sujeita a estes
              Termos.
            </p>
          </Section>

          <Section title="2. Aceitação dos termos">
            <p>
              Ao acessar e utilizar o site, o usuário declara estar ciente destes Termos e
              compromete-se a utilizá-lo de acordo com suas disposições e a legislação aplicável.
              Caso não concorde, deverá interromper a utilização do site.
            </p>
          </Section>

          <Section title="3. Caráter das informações">
            <p>
              As informações do site possuem caráter predominantemente institucional. Salvo
              indicação expressa, não constituem proposta irrevogável de contratação.
            </p>
          </Section>

          <Section title="4. Preços, promoções e condições comerciais">
            <p>
              Os preços, condições e promoções divulgados no site serão aqueles expressamente
              informados na respectiva oferta, campanha ou comunicação, observados os prazos,
              condições e limitações nela estabelecidos e a legislação aplicável. A contratação dos
              serviços estará sujeita às condições apresentadas no momento da contratação e ao
              respectivo contrato de prestação de serviços, sem prejuízo dos direitos assegurados
              pela legislação consumerista.
            </p>
          </Section>

          <Section title="5. Treino experimental">
            <p>
              O preenchimento do formulário de treino experimental não representa, por si só,
              confirmação de horário, garantia de vaga, matrícula ou obrigação de contratação. O
              agendamento estará sujeito à disponibilidade, aos critérios operacionais da unidade e
              à confirmação pela Nook Gym. A participação no treino experimental poderá estar
              condicionada ao preenchimento de informações e documentos necessários à segurança da
              atividade, inclusive PAR-Q, declarações ou outros documentos aplicáveis.
            </p>
            <p>
              A Nook Gym poderá estabelecer critérios de disponibilidade, horários, idade,
              quantidade de participantes e demais condições necessárias à organização e segurança do
              treino experimental.
            </p>
            <p>
              Os dados fornecidos no formulário serão tratados de acordo com a Política de
              Privacidade da Nook Gym.
            </p>
          </Section>

          <Section title="6. Utilização adequada do site">
            <p>
              É proibido utilizar o site para finalidade ilícita, violar direitos de terceiros,
              tentar acesso não autorizado, introduzir códigos maliciosos, explorar vulnerabilidades,
              realizar coleta automatizada indevida de conteúdo ou utilizar a marca Nook Gym sem
              autorização.
            </p>
          </Section>

          <Section title="7. Disponibilidade do site">
            <p>
              A Nook Gym empregará esforços razoáveis para manter o site disponível, mas ele poderá
              sofrer interrupções decorrentes de manutenção, falhas técnicas, força maior ou outras
              circunstâncias fora de seu controle razoável.
            </p>
          </Section>

          <Section title="8. Propriedade intelectual">
            <p>
              A marca Nook Gym, seus nomes, logotipos, identidade visual, textos, fotografias,
              vídeos, materiais gráficos, elementos de design, conteúdos, funcionalidades e demais
              elementos disponibilizados no site são protegidos pela legislação aplicável e, quando
              pertencentes à Nook Gym, são de sua titularidade ou utilizados mediante autorização.
            </p>
            <p>
              É proibida a reprodução, distribuição, modificação, publicação, disponibilização,
              exploração comercial ou utilização desses elementos sem autorização prévia e expressa
              da Nook Gym ou do respectivo titular, quando aplicável.
            </p>
            <p>
              A utilização do site não confere ao usuário qualquer licença ou direito de
              propriedade sobre a marca, conteúdos ou demais elementos protegidos.
            </p>
          </Section>

          <Section title="9. Informações sobre saúde e treinamento">
            <p>
              As informações sobre atividade física e saúde disponibilizadas no site têm caráter
              geral e não constituem diagnóstico, avaliação ou prescrição médica, nem substituem
              acompanhamento profissional.
            </p>
          </Section>

          <Section title="10. Contratação dos serviços">
            <p>
              A utilização do site não implica, por si só, contratação dos serviços da Nook Gym.
            </p>
            <p>
              A contratação estará sujeita às condições comerciais apresentadas ao interessado e à
              formalização do respectivo contrato de prestação de serviços, quando aplicável, além
              do Regulamento Interno, PAR-Q, Termo de Responsabilidade e demais documentos integrantes
              da relação contratual.
            </p>
            <p>
              Em caso de contratação, serão observadas as disposições do respectivo instrumento
              contratual e da legislação aplicável, especialmente as normas de proteção e defesa do
              consumidor.
            </p>
          </Section>

          <Section title="11. Links e plataformas de terceiros">
            <p>
              O site poderá disponibilizar links para WhatsApp, Instagram, Google Maps, plataformas
              de agendamento e outros serviços de terceiros, sujeitos às respectivas políticas.
            </p>
          </Section>

          <Section title="12. Proteção de dados pessoais">
            <p>
              O tratamento de dados pessoais realizado em razão da utilização do site será feito de
              acordo com a Política de Privacidade da Nook Gym, disponível em página própria.
            </p>
          </Section>

          <Section title="13. Responsabilidade">
            <p>
              A Nook Gym não será responsável por falhas decorrentes exclusivamente de serviços de
              terceiros ou circunstâncias fora de seu controle razoável, sem prejuízo dos direitos
              assegurados ao consumidor pela legislação brasileira.
            </p>
          </Section>

          <Section title="14. Alteração dos termos">
            <p>
              A Nook Gym poderá atualizar estes Termos periodicamente para refletir alterações no
              funcionamento do site, nos serviços disponibilizados ou na legislação aplicável.
            </p>
            <p>
              A versão vigente será disponibilizada nesta página, acompanhada da respectiva data de
              atualização.
            </p>
            <p>
              As alterações não prejudicarão direitos já adquiridos ou obrigações decorrentes de
              contratos celebrados anteriormente, quando aplicável.
            </p>
          </Section>

          <Section title="15. Violação dos termos">
            <p>
              Em caso de utilização em desacordo com estes Termos, a Nook Gym poderá adotar as
              medidas necessárias para interromper ou prevenir a utilização indevida.
            </p>
          </Section>

          <Section title="16. Legislação aplicável">
            <p>Estes Termos são regidos pela legislação brasileira.</p>
          </Section>

          <Section title="17. Contato">
            <p>
              NOOK GYM ACADEMIA LTDA | CNPJ: 55.440.324/0001-32 | Rua Luiza Nogueira Gonçalves
              Prof., nº 00350, Blc 1, Lojas 102/103/121/122, Recreio dos Bandeirantes, Rio de
              Janeiro/RJ, CEP 22.790-465 | E-mail:{" "}
              <a href="mailto:contato@nookgym.com.br" className="text-nook-green hover:underline">
                contato@nookgym.com.br
              </a>{" "}
              | Última atualização: {LAST_UPDATE}
            </p>
          </Section>
        </div>
      </div>
    </section>
  );
}
